import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../../components/Navbar';
import OnboardingOverlay from '../../components/OnboardingOverlay';
import ProviderOnboarding from '../../components/ProviderOnboarding';
import ProfileStrengthWidget from '../../components/ProfileStrengthWidget';
import SubscriptionManagement from '../../components/SubscriptionManagement';
import ReferralProgram from '../../components/ReferralProgram';
import AccountSettings from '../../components/AccountSettings';
import ProviderStatistics from '../../components/ProviderStatistics';
import ProviderProfileEdit from '../../components/ProviderProfileEdit';
import ProfileSettings from '../../components/ProfileSettings';
import ProviderPartners from '../../components/ProviderPartners';
import ReviewInvitations from '../../components/ReviewInvitations';
import BillingSettings from '../../components/BillingSettings';
import MediaGallery from '../../components/MediaGallery';
import { getImageUrl } from '../../utils/imageUrl';
import usePageTitle from '../../hooks/usePageTitle';
import { Edit, Eye, EyeOff, Mail, Calendar, MapPin, CheckCircle, XCircle, AlertTriangle, Gift, Settings, FileEdit, Users, Image, Phone, Trash2, Star, Loader } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import ChatDialog from '../../components/ChatDialog';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderDashboard = ({ user, onLogout, onUpdateUser }) => {
  const navigate = useNavigate();
  usePageTitle('Dienstleister-Dashboard');
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [chatRequest, setChatRequest] = useState(null);
  const { byRequest, total: unreadTotal, refresh: refreshUnread } = useUnreadMessages();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      // allSettled statt all: ein einzelner Ausfall (z.B. Profil) darf nicht
      // das ganze Dashboard leeren und die Anfragenliste verstecken.
      const [profileRes, requestsRes] = await Promise.allSettled([
        axios.get(`${API}/provider/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/provider/requests`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (profileRes.status === 'fulfilled') {
        setProfile(profileRes.value.data);
      } else {
        console.error('Error loading profile:', profileRes.reason);
      }
      if (requestsRes.status === 'fulfilled') {
        setRequests(requestsRes.value.data);
      } else {
        console.error('Error loading requests:', requestsRes.reason);
      }

      if (profileRes.status === 'rejected' || requestsRes.status === 'rejected') {
        toast.error('Einige Daten konnten nicht geladen werden.');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Möchtest du diese Anfrage wirklich löschen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/provider/requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRequests(requests.filter(req => req.id !== requestId));
      toast.success('Anfrage erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Fehler beim Löschen der Anfrage');
    }
  };

  const toggleVisibility = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/provider/profile`,
        { is_visible: !profile.is_visible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setProfile({ ...profile, is_visible: !profile.is_visible });
      toast.success(profile.is_visible ? 'Profil versteckt' : 'Profil sichtbar');
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('Fehler beim Ändern der Sichtbarkeit');
    }
  };

  const activateSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/provider/subscription/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Abo aktiviert (Mock)');
      await loadData();
    } catch (error) {
      console.error('Activation error:', error);
      toast.error('Fehler bei der Aktivierung');
    }
  };

  const cancelSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/provider/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Abo gekündigt (Mock)');
      await loadData();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Fehler bei der Kündigung');
    }
  };

  const getSubscriptionBadge = (status) => {
    const badges = {
      trial: { text: 'Testphase', color: 'bg-yellow-100 text-yellow-800' },
      active: { text: 'Aktiv', color: 'bg-green-100 text-green-800' },
      expired: { text: 'Abgelaufen', color: 'bg-red-100 text-red-800' }
    };
    const badge = badges[status] || badges.trial;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-[#2B2142]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF0D1]/30 via-white to-[#F4F2F2]">
      <OnboardingOverlay user={user} profileComplete={profile?.is_complete} />
      <ProviderOnboarding user={user} profile={profile} />
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="pt-24 pb-12 px-4">
        <div className="w-full max-w-6xl mx-auto">
          {/* Header - Stacked on Mobile to prevent overlap */}
          <div className="px-4 sm:px-6 pt-4 pb-3 sm:pb-0">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center sm:gap-4 mb-4 sm:mb-8">
              <div className="w-full sm:w-auto">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-1">Dashboard</h1>
                <p className="text-sm sm:text-base text-gray-600">Verwalte dein Profil und Anfragen</p>
              </div>
              <button
                onClick={() => navigate('/provider/onboarding')}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl text-sm sm:text-base min-h-[44px]"
                data-testid="edit-profile-btn"
              >
                <Edit className="w-4 h-4" />
                <span>Kategorie bearbeiten</span>
              </button>
            </div>
          </div>

          {/* Missing Required Fields Warning */}
          {profile?.missing_required_fields && profile.missing_required_fields.length > 0 && (
            <div className="mb-4 sm:mb-8 p-4 sm:p-6 bg-orange-50 border-2 border-orange-200 rounded-none sm:rounded-2xl mx-0 sm:mx-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Dein Profil ist nicht sichtbar</h3>
                  <p className="text-xs sm:text-sm text-gray-700 mb-3">
                    Um in der Suche angezeigt zu werden, fülle bitte folgende Pflichtfelder aus:
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {profile.missing_required_fields.map((field, idx) => (
                      <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-800 text-xs sm:text-sm rounded-lg font-medium">
                        {field}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => setActiveTab('edit')}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg transition-colors text-sm sm:text-base"
                  >
                    Profil vervollständigen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Visibility Warning (Subscription-related) */}
          {profile && !profile.is_visible && (!profile.missing_required_fields || profile.missing_required_fields.length === 0) && (
            <div className="mb-4 sm:mb-8 p-4 sm:p-6 bg-yellow-50 border-2 border-yellow-200 rounded-none sm:rounded-2xl mx-0 sm:mx-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Dein Profil ist nicht sichtbar</h3>
                  <p className="text-xs sm:text-sm text-gray-700 mb-3 sm:mb-4">
                    {profile.subscription_status === 'none' || !profile.subscription_status ? (
                      <>Dein Profil ist aktuell nicht sichtbar. Bitte wähle einen Plan, um bei EventConnect gefunden zu werden.</>
                    ) : profile.subscription_status === 'suspended' ? (
                      <>Wir konnten deine Zahlung nicht abbuchen. Bitte aktualisiere deine Zahlungsdaten, um wieder sichtbar zu sein.</>
                    ) : profile.subscription_status === 'expired' ? (
                      <>Dein Abo ist abgelaufen. Bitte aktualisiere deine Zahlungsdaten oder wähle einen neuen Plan.</>
                    ) : profile.profil_status === 'entwurf' ? (
                      <>Dein Profil befindet sich noch im Entwurf-Modus. Schließe ein Abo ab, um sichtbar zu werden.</>
                    ) : (
                      <>Dein Profil ist aus unbekannten Gründen nicht sichtbar. Bitte kontaktiere den Support.</>
                    )}
                  </p>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-colors text-sm sm:text-base"
                  >
                    Plan auswählen
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6 mt-4 sm:mt-8">
            {/* Tabs Container - Full width on mobile */}
            <div className="w-full">
              <div className="overflow-x-auto px-4 sm:px-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <TabsList className="bg-white rounded-xl p-1.5 sm:p-2 shadow-md inline-flex gap-0.5 sm:gap-1 min-w-full w-max">
                  <TabsTrigger value="overview" data-testid="tab-overview" className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3 py-2">
                    Übersicht
                  </TabsTrigger>
                  <TabsTrigger value="edit" data-testid="tab-edit" className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3 py-2">
                    <FileEdit className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block mr-1" />
                    <span className="hidden sm:inline">Profil bearbeiten</span>
                    <span className="sm:hidden">Profil</span>
                  </TabsTrigger>
                  <TabsTrigger value="media" data-testid="tab-media" className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3 py-2">
                    <Image className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block mr-1" />
                    Medien
                  </TabsTrigger>
                  <TabsTrigger value="partners" data-testid="tab-partners" className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3 py-2">
                    <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block mr-1" />
                    Partner
                  </TabsTrigger>
                  <TabsTrigger value="reviews" data-testid="tab-reviews" className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3 py-2">
                    <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block mr-1" />
                    <span className="hidden sm:inline">Bewertungen</span>
                    <span className="sm:hidden">Bew.</span>
                  </TabsTrigger>
                  <TabsTrigger value="requests" data-testid="tab-requests" className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3 py-2">
                    <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block mr-1" />
                    Anfragen ({requests.length})
                    {unreadTotal > 0 && (
                      <span className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F7B693] text-[#2B2142] text-[10px] font-bold inline-flex items-center justify-center align-middle">
                        {unreadTotal > 99 ? '99+' : unreadTotal}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="subscription" data-testid="tab-subscription" className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3 py-2">
                    Abo
                  </TabsTrigger>
                  <TabsTrigger value="statistics" data-testid="tab-statistics" className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3 py-2">
                    <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block mr-1" />
                    <span className="hidden sm:inline">Statistiken</span>
                    <span className="sm:hidden">Stats</span>
                  </TabsTrigger>
                  <TabsTrigger value="referral" data-testid="tab-referral" className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3 py-2">
                    <Gift className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block mr-1" />
                    <span className="hidden sm:inline">Empfehlungen</span>
                    <span className="sm:hidden">Emf.</span>
                  </TabsTrigger>
                  <TabsTrigger value="settings" data-testid="tab-settings" className="whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-3 py-2">
                    <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline-block mr-1" />
                    <span className="hidden sm:inline">Einstellungen</span>
                    <span className="sm:hidden">Einstell.</span>
                  </TabsTrigger>
                </TabsList>
              </div>
            </div>
            
            <TabsContent value="overview" data-testid="overview-content">
              {/* Profile Strength Widget - Full width on mobile */}
              <div className="mb-4 sm:mb-6 px-4 sm:px-6">
                <ProfileStrengthWidget profile={profile} />
              </div>

              <div className="bg-white rounded-none sm:rounded-3xl shadow-xl p-4 sm:p-6 md:p-8 mx-0 sm:mx-6">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="w-full sm:w-auto">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2">{profile?.company_name || 'Kein Name'}</h2>
                    <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                      {profile?.categories?.map((cat) => (
                        <span key={cat} className="px-2.5 sm:px-3 py-0.5 sm:py-1 bg-[#FFF0D1] text-[#2B2142] rounded-full text-xs sm:text-sm font-semibold">
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
                    {profile?.is_complete ? (
                      <span className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs sm:text-sm font-semibold">
                        <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        Vollständig
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs sm:text-sm font-semibold">
                        <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        Unvollständig
                      </span>
                    )}
                    <button
                      onClick={toggleVisibility}
                      className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl font-semibold text-xs sm:text-sm flex-1 sm:flex-initial justify-center ${
                        profile?.is_visible
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                      data-testid="toggle-visibility-btn"
                    >
                      {profile?.is_visible ? (
                        <><Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Sichtbar</>
                      ) : (
                        <><EyeOff className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Versteckt</>
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <MapPin className="w-4 h-4" />
                      <span className="font-semibold text-sm sm:text-base">Standort</span>
                    </div>
                    <p className="text-gray-800 text-sm sm:text-base">
                      {profile?.location_city}, {profile?.location_zip}
                      {profile?.location_state && `, ${profile.location_state}`}
                    </p>
                  </div>
                  
                  <div>
                    <p className="font-semibold text-gray-600 mb-2 text-sm sm:text-base">Kurzbeschreibung</p>
                    <p className="text-gray-800 text-sm sm:text-base leading-relaxed">{profile?.short_description || 'Keine Beschreibung'}</p>
                  </div>
                  
                  {profile?.long_description && (
                    <div className="overflow-hidden">
                      <p className="font-semibold text-gray-600 mb-2 text-sm sm:text-base">Langbeschreibung</p>
                      <p className="text-gray-800 text-sm sm:text-base leading-relaxed whitespace-pre-line break-words">{profile.long_description}</p>
                    </div>
                  )}
                  
                  {profile?.price_range && (
                    <div>
                      <p className="font-semibold text-gray-600 mb-2 text-sm sm:text-base">Preisrahmen</p>
                      <p className="text-gray-800 font-semibold text-sm sm:text-base">{profile.price_range}</p>
                    </div>
                  )}
                  
                  {profile?.images && profile.images.length > 0 && (
                    <div>
                      <p className="font-semibold text-gray-600 mb-3 text-sm sm:text-base">Portfolio ({profile.images.length} Bilder)</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                        {profile.images.map((img, index) => (
                          <img
                            key={img}
                            src={getImageUrl(img)}
                            alt={`Portfolio ${index + 1}`}
                            className="w-full aspect-square object-cover rounded-lg shadow-sm"
                            onError={(e) => {
                              console.error('Dashboard image load error:', img);
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EBild%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4">
                    {profile?.instagram_link && (
                      <a
                        href={profile.instagram_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl font-semibold hover:from-pink-600 hover:to-purple-700 transition-colors text-sm sm:text-base w-full sm:w-auto justify-center shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                        Instagram
                      </a>
                    )}
                    {profile?.website_link && (
                      <a
                        href={profile.website_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#2B2142] to-[#3D3158] text-white rounded-xl font-semibold hover:from-[#2B2142] hover:to-[#2B2142] transition-colors text-sm sm:text-base w-full sm:w-auto justify-center shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                        Website
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="edit" data-testid="edit-content">
              <ProviderProfileEdit />
            </TabsContent>
            
            <TabsContent value="media" data-testid="media-content">
              <MediaGallery />
            </TabsContent>
            
            <TabsContent value="partners" data-testid="partners-content">
              <ProviderPartners />
            </TabsContent>

            <TabsContent value="reviews" data-testid="reviews-content">
              <ReviewInvitations />
            </TabsContent>

            <TabsContent value="requests" data-testid="requests-content">
              <div className="space-y-4">
                {requests.length === 0 ? (
                  <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
                    <Mail className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Noch keine Anfragen</h3>
                    <p className="text-gray-600">Sobald Kunden dich kontaktieren, erscheinen die Anfragen hier.</p>
                  </div>
                ) : (
                  requests.map((req) => (
                    <div key={req.id} className="bg-white rounded-2xl shadow-xl p-6" data-testid={`request-${req.id}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900">{req.customer_name}</h3>
                          <p className="text-gray-600">{req.customer_email}</p>
                          {req.customer_phone && (
                            <div className="flex items-center gap-2 text-gray-600 mt-1">
                              <Phone className="w-4 h-4" />
                              <span>{req.customer_phone}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 items-start">
                          <span className="px-3 py-1 bg-[#FFF0D1] text-[#2B2142] rounded-full text-sm font-semibold">
                            {req.event_type}
                          </span>
                          <button
                            onClick={() => handleDeleteRequest(req.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Anfrage löschen"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-gray-700">
                        {req.event_date && (
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Datum: {req.event_date}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>Ort: {req.event_location}</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm font-semibold text-gray-600 mb-1">Nachricht:</p>
                        <p className="text-gray-800">{req.message}</p>
                      </div>
                      
                      <div className="mt-4 flex items-center justify-between gap-3">
                        <p className="text-sm text-gray-500">
                          Erhalten am: {new Date(req.created_at).toLocaleDateString('de-DE')}
                        </p>
                        <button
                          onClick={() => setChatRequest(req)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-lg transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          Chat öffnen
                          {byRequest[req.id] > 0 && (
                            <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#F7B693] text-[#2B2142] text-xs font-bold flex items-center justify-center">
                              {byRequest[req.id] > 99 ? '99+' : byRequest[req.id]}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="subscription" data-testid="subscription-content">
              <SubscriptionManagement user={user} />
            </TabsContent>
            
            <TabsContent value="statistics" data-testid="statistics-content">
              <ProviderStatistics />
            </TabsContent>

            <TabsContent value="referral" data-testid="referral-content">
              <ReferralProgram />
            </TabsContent>

            <TabsContent value="settings" data-testid="settings-content">
              <div className="space-y-6">
                <BillingSettings />
                <ProfileSettings user={user} onUpdateUser={onUpdateUser} />
                <AccountSettings user={user} onLogout={onLogout} />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <ChatDialog
        request={chatRequest}
        open={!!chatRequest}
        onOpenChange={(open) => {
          if (!open) {
            setChatRequest(null);
            loadData();
          }
        }}
        title={chatRequest?.customer_name || 'Anfrage'}
        subtitle={chatRequest ? `${chatRequest.event_type} · ${chatRequest.event_location}` : ''}
        onActivity={refreshUnread}
      />
    </div>
  );
};

export default ProviderDashboard;
